import { Box, Radio, Typography, visuallyHidden } from '@montugroup/design-system';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { useCallback } from 'react';

import type { Price } from '@montugroup/circuit-shared/types/product';
import { formatPrice } from './util';

interface RefillDoseOptionProps {
  quantity: number;
  setQuantity: (qty: number) => void;
  maxQuantity: number;
  prescriptionPeriodDays: number;
  pricePerUnit: Price;
  isSelected: boolean;
  singleOption?: boolean;
  isDisabled?: boolean;
}

interface DoseDisclaimerParams {
  maxQuantity: number;
  prescriptionPeriodDays: number;
}

export const generateMaxDoseDisclaimer = (params: DoseDisclaimerParams) => {
  const { maxQuantity, prescriptionPeriodDays } = params;
  return `Your prescription lets you order a maximum of ${maxQuantity} ${
    maxQuantity === 1 ? 'unit' : 'units'
  } within a ${prescriptionPeriodDays}-day period`;
};

export const generateReducedDoseDisclaimer = (params: DoseDisclaimerParams) => {
  const { prescriptionPeriodDays } = params;
  return `If you purchase a reduced dose you can still order the remainder of your prescribed dose within ${prescriptionPeriodDays} days`;
};

const DoseOptionContainer = styled(Box)(({ theme }) => ({
  border: '1px solid',
  borderRadius: theme.shape.borderRadius * 2,
  borderColor: theme.palette.action.disabled,
  columnGap: theme.spacing(1),
  padding: theme.spacing(2),
  paddingRight: theme.spacing(4), // The radio control takes up space on the left. Adjusting withspacing on the right.
  paddingBottom: theme.spacing(4), // The radio control takes up space on the top. Adjusting withspacing on the bottom.
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gridTemplateRows: 'auto auto',
  gridTemplateAreas: `"control header"
  ". content"`,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: alpha(
      theme.palette.action.selected,
      0.04,
    ) /* Note, there isn't the desired theme colour available. Using Alpha of of the selected state */,
  },
  transition: theme.transitions.create('background', {
    easing: theme.transitions.easing.easeIn,
    duration: theme.transitions.duration.shortest,
  }),
  ['&.single']: {
    border: 'none',
    backgroundColor: 'transparent!important',
    paddingLeft: 0,
    paddingRight: 0,
  },
  ['&.disabled']: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

const DoseOptionControls = styled(Box)(() => ({
  gridArea: 'control',
}));

const DoseOptionContent = styled(Box)(({ theme }) => ({
  gridArea: 'content',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  marginTop: theme.spacing(2),
}));

const DoseOptionHeader = styled(Box)(() => ({
  gridArea: 'header',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const DoseOptionDisclaimer = styled(Box)(() => ({}));

export function RefillDoseOption(props: RefillDoseOptionProps) {
  const theme = useTheme();
  const {
    quantity,
    maxQuantity,
    prescriptionPeriodDays,
    pricePerUnit,
    isSelected,
    setQuantity,
    singleOption,
    isDisabled,
  } = props;

  const price = formatPrice(Number(pricePerUnit) * quantity);

  const isMaxDose = quantity === maxQuantity;
  const quantityLabel = `${quantity} ${quantity === 1 ? 'unit' : 'units'}`;
  const disclaimer = isMaxDose
    ? generateMaxDoseDisclaimer({ maxQuantity, prescriptionPeriodDays })
    : generateReducedDoseDisclaimer({ maxQuantity, prescriptionPeriodDays });

  const onClick = useCallback(() => {
    if (!isDisabled) {
      setQuantity(quantity);
    }
  }, [setQuantity, quantity, isDisabled]);

  return (
    <DoseOptionContainer
      className={`${singleOption ? 'single' : ''} ${isDisabled ? 'disabled' : ''}`}
      sx={{ backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.background.default }}
      onClick={onClick}
      data-testid="RefillDoseOptionContainer"
      aria-disabled={isDisabled}
    >
      <DoseOptionControls>
        {singleOption ? null : (
          <>
            <Radio id={`dose-option-${quantity}`} checked={isSelected} disabled={isDisabled} />
            <label htmlFor={`dose-option-${quantity}`} style={visuallyHidden}>
              Select {quantity} {quantity === 1 ? 'unit' : 'units'} for {price}
            </label>
          </>
        )}
      </DoseOptionControls>
      <DoseOptionHeader>
        <Typography aria-hidden>
          <Box component="span" fontWeight="bold">
            {quantityLabel}
          </Box>
        </Typography>
        <Typography fontWeight="bold">{price}</Typography>
      </DoseOptionHeader>
      {prescriptionPeriodDays > 0 && (
        <DoseOptionContent>
          <DoseOptionDisclaimer>
            <Typography>{disclaimer}</Typography>
          </DoseOptionDisclaimer>
        </DoseOptionContent>
      )}
    </DoseOptionContainer>
  );
}

export default RefillDoseOption;
